import * as React from 'react';
import { ChangeEvent, useEffect, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import { Form } from 'react-bootstrap';

import { Loader } from '@/components/Loader/Loader';

import { FileUploader } from './FileUploader';
import { useAuth } from '@/lib/auth';
import { showNotification } from '@/utils/notificationUtils';
import { useUpdateParentProfile, useUploadPicture } from '../api/updateParentProfile';
import { AddressAutocomplete } from './AddressAutocomplete';

import '../routes/Appointments.scss';
import './ParentProfile.scss';
import { useLocation } from 'react-router-dom';
import Utils from '@/utils/util';
import { ReactMultiEmail } from '@/components/multiemail';
import { isArray, set } from 'lodash';
import { BackNav } from '@/components/BackNavigation/BackNavigation';

import { useTranslation } from 'react-i18next';
type Props = {
  fromMenu?: boolean;
  onSuccess?: () => void;
  onNext?: (page: 'pet-profile') => void;
  onBackClick?: () => void;
};
type ParentData = {
  fName: string;
  lName: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  apt?: string;
  lat?: number;
  lng?: number;
  country: string;
  imageUrl?: string;
  optOutIvr?: boolean;
  optOutSms?: boolean;
  name?: string;
  altEmails?:string[],
  id?: string;
};
export const ParentProfile = ({ onNext, onBackClick, fromMenu }: Props) => {
  const location = useLocation();
  const { t } = useTranslation();
  const [countries, setCountries] = useState([
    {
      label: 'India',
      value: 'India',
      isSelected: false,
    },
    {
      label: 'USA',
      value: 'USA',
      isSelected: false,
    },
  ]);
  const { user, refetchUser } = useAuth();
  const [petParentData, setPetParentData] = useState<ParentData>({
    fName: '',
    lName: '',
    phone: '',
    altEmails: [],
    address: '',
    city: '',
    state: '',
    zip: '',
    apt: '',
    country: 'US',
    imageUrl: '',
    optOutIvr: false,
    optOutSms: false,
    name: '',
    id: '',
  });
  const updateParentProfileMutation = useUpdateParentProfile();
  const updateProfilePicMutation = useUploadPicture();
  const [googleData, setGoogleData] = useState();
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  useEffect(() => {
    Utils.scrollToTop();
  }, []);

  useEffect(() => {
    if (user) {
      petParentData.fName = user?.firstName;
      petParentData.name = user?.name;
      petParentData.lName = user?.lastName;
      petParentData.phone = user?.phone;
      petParentData.address = user?.address;
      petParentData.city = user?.city;
      petParentData.state = user?.state;
      petParentData.zip = user?.zip;
      petParentData.apt = user?.apt;
      petParentData.imageUrl = user?.imageUrl;
      petParentData.altEmails = user?.altEmails;
      petParentData.country = 'US';
      petParentData.optOutIvr = !!user?.optOutIvr;
      petParentData.optOutSms = !!user?.optOutSms;
      petParentData.id = user?.id ? user.id : '';
      petParentData.lat = user?.lat;
      petParentData.lng = user?.lng;
      if (user?.name) {
        const names = user?.name.split(' ');
        petParentData.fName = names && names.length > 0 ? names[0] : user?.firstName;
        petParentData.lName = names && names.length > 0 ? names[1] : user?.lastName;
      }

      setPetParentData({ ...petParentData });
    }
    if(user?.phone) {
      setIsPhoneValid(true);
    } else {
      setIsPhoneValid(false);
    }
  }, [user]);

  React.useEffect(() => {
    if (googleData) {
      const val = googleData as any;
      petParentData.address = `${val.streetNumber} ${val.name}`;
      petParentData.city = val.city;
      petParentData.state = val.state;
      petParentData.zip = val.zipCode;
      petParentData.apt = val.aptNumber;
      petParentData.lat = val.lat;
      petParentData.lng = val.lng;
      setPetParentData({ ...petParentData });
    }
  }, [googleData]);

  const validate = () => {
    let errorMessage = undefined;
    const { fName, name, phone, address, country, zip } = petParentData;
    if (!name || name.trim().length < 1) {
      errorMessage = t('Please enter name');
    } else if (!phone || phone.trim().length < 1) {
      errorMessage = t('Please enter valid phone number');
    } else if(phone) {
      if(!isPhoneValid) {
        errorMessage = t('Please enter valid phone number');
      }
    } else if (!address || address.trim().length < 1) {
      errorMessage = t('Please enter address');
    } else if (!zip || zip.trim().length < 1) {
      errorMessage = t('Please enter zip code');
    }
    if (errorMessage) {
      showNotification({
        type: 'info',
        title: errorMessage,
      });
      return false;
    } else {
      return true;
    }
  };

  const onProfilePicUpload = async (formData: FormData) => {
    const response = await updateProfilePicMutation.mutateAsync({
      file: formData,
    });
    onFormValueChange('imageUrl', response ? response.imageUrl : '');
  };
  const onPhoneFieldChange = (
    value: string ,
    country: any
  ) => {
    if(value === country.dialCode)  {
      setIsPhoneValid(false);
    } else {
      (petParentData as any)['phone'] = value;
      setIsPhoneValid(true);
      setPetParentData({ ...petParentData });
    }
  }
  
  const onFormValueChange = (formFieldName: string, value: any) => {
    (petParentData as any)[formFieldName] = value;
    setPetParentData({ ...petParentData });
  };
  const onFormValueChangeEmails = (formFieldName: string, value: string[]) => {
    petParentData.altEmails = value;
    setPetParentData({ ...petParentData });
  };
  const submitParentData = async () => {
    if (validate() && !updateParentProfileMutation.isLoading) {
      const { id, fName, name, lName, phone, address, city, state, zip, apt, country, imageUrl, altEmails, lat, lng } =
        petParentData;
      await updateParentProfileMutation.mutateAsync({
        data: {
          id: id,
          name: name,
          firstName: fName,
          phone: phone,
          lastName: lName,
          address,
          city,
          state,
          zip,
          apt,
          country,
          lat,
          lng,
          altEmails: altEmails ? altEmails : [],
          optOutIvr: !!petParentData.optOutIvr,
          optOutSms: !!petParentData.optOutSms,
          imageUrl: imageUrl ? imageUrl : '',
        },
      });
      refetchUser();
      onNext && onNext('pet-profile');
    }
  };

  const showUploadProfile = location.pathname === '/my-profile' ? false : true;
  return (
    <section className="parent-profile">
      <BackNav onClick={() => onBackClick && onBackClick()} />
      <div className="profile-container">
        {showUploadProfile && (
          <>
            <h5>{t('Pet Parent Profile')}</h5>
            <FileUploader
              key={'parent-profile-pic'}
              onUpload={onProfilePicUpload}
              url={petParentData.imageUrl}
              isUploading={updateProfilePicMutation.isLoading}
            />
          </>
        )}
        <div className="petsked-form">
          <div className="form-field personal">
            <label className="form-label">{t('Add your personal details')}</label>
            <div className="split">
              <input
                type="text"
                value={petParentData.name}
                placeholder="Name"
                onInput={(e: any) => onFormValueChange('name', e.target?.value)}
              />
              <PhoneInput
                key={'parent-profile-tel'}
                country={'us'}
                value={petParentData.phone}
                onChange={onPhoneFieldChange}
                countryCodeEditable={false}
              />
            </div>
            <div>
              <ReactMultiEmail
                placeholder={t("Additional Emails")}
                emails={petParentData?.altEmails}
                onChange={(_emails: string[]) => {
                  onFormValueChangeEmails('altEmails', _emails);
                }}
                autoFocus={false}
                getLabel={(email: any, index: number, removeEmail: any) => {
                  return (
                    <div data-tag key={index}>
                      <div data-tag-item>{email}</div>
                      <span data-tag-handle onClick={() => removeEmail(index)}>
                        ×
                      </span>
                    </div>
                  );
                }}
              />
            </div>
          </div>
          <div className="form-field additional">
            <label className="form-label">{t('Enter some extra details below')}</label>
            <div>
              <AddressAutocomplete
                id="address"
                value={petParentData.address}
                onChange={(ev) => {
                  const val = ev.target.value as any;
                  setGoogleData(val);
                }}
                onTextChange={(ev: ChangeEvent<HTMLInputElement>) =>
                  onFormValueChange('address', ev.target?.value)
                }
                placeholder={t("Enter address lane")}
              />
            </div>
            <div className="split">
              <input
                type="text"
                value={petParentData.apt}
                placeholder={t("Apt")}
                onInput={(e: any) => onFormValueChange('apt', e.target?.value)}
                //onInput={(e: any) => onFormValueChange('lName', e.target?.value)}
              />
              <input
                value={petParentData.city}
                type="text"
                placeholder={t("City")}
                onInput={(e: any) => onFormValueChange('city', e.target?.value)}
              />
            </div>
            <div className="split">
              <input
                type="text"
                value={petParentData.state}
                placeholder={t("State")}
                onInput={(e: any) => onFormValueChange('state', e.target?.value)}
              />
              <input
                value={petParentData.zip}
                type="text"
                placeholder={t("Zip code")}
                onInput={(e: any) => onFormValueChange('zip', e.target?.value)}
              />
            </div>
            <div className="split">
              <input
                type="text"
                value={petParentData.country}
                placeholder={t("Country")}
                disabled
                //onInput={(e: any) => onFormValueChange('lName', e.target?.value)}
              />
            </div>
            <hr />
            <div className="features">
              <div className="feature">
                <div className="detail">
                  <label>{t('Enable SMS Notification')}</label>
                  <p>{t('By enabling this option, you agree to receive SMS messages from Petsked regarding your appointments and room bookings. Message frequency varies. Message & data rates may apply. Text STOP to unsubscribe.')}</p>
                </div>
                <Form.Check // prettier-ignore
                  type="switch"
                  id="sms"
                  checked={!petParentData.optOutSms}
                  onChange={(e: any) => onFormValueChange('optOutSms', !e.target?.checked)}
                />
              </div>
              <hr />
              <div className="feature">
                <div className="detail">
                  <label>{t('Enable Calling Notification')}</label>
                  {/* <p>Consectetur adipiscing elit, sed eiusmod tempor</p> */}
                </div>
                <Form.Check // prettier-ignore
                  type="switch"
                  id="call-notification"
                  checked={!petParentData.optOutIvr}
                  onChange={(e: any) => onFormValueChange('optOutIvr', !e.target?.checked)}
                />
              </div>
              <hr />
              <div className="action mb-4">
                <button
                  className="petsked-button button-dark"
                  onClick={submitParentData}
                  disabled={updateParentProfileMutation.isLoading}
                >
                  {updateParentProfileMutation.isLoading ? (
                    <Loader />
                  ) : (
                    <span>{showUploadProfile ? t('Continue') : t('Update Profile')}</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
